import { css } from '@emotion/react';
import { aigleColor } from './colorStyleGuide';

/**
 * Input 스타일 가이드
 * 
 * 애플리케이션 전체에서 사용되는 Input 관련 스타일을 관리합니다.
 * Text Input, Password, Select, Search, Checkbox, Radio 등을 제공합니다.
 * 
 * @example
 * import { aigleInputStyles } from '@styles/inputStyleGuide';
 * 
 * const MyInput = styled.input`
 *   ${aigleInputStyles.base}
 *   ${aigleInputStyles.state.default}
 * `;
 */

/**
 * Input 기본 스타일
 */
const baseInputStyle = css`
  /* 브라우저 기본 스타일 제거 */
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  margin: 0;
  box-shadow: none;
  
  /* 기본 레이아웃 */
  display: flex;
  align-items: center;
  width: 100%;
  padding: 14px 16px;
  border-radius: 4px;
  box-sizing: border-box;
  
  /* 텍스트 스타일 */
  font-size: 16px;
  line-height: 140%;
  color: ${aigleColor.text.primary};
  
  /* Placeholder 스타일 */
  &::placeholder {
    color: ${aigleColor.text.caption};
    opacity: 1;
  }
  
  /* 인터랙션 */
  transition: all 0.2s ease-in-out;
  outline: none;

  &:focus {
    outline: none;
  }
`;

/**
 * Input Container 스타일 (Label + Input + Message를 포함하는 Wrapper)
 */
export const inputContainer = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 4px;
`;

/**
 * Label 스타일
 */
export const inputLabel = css`
  font-size: 14px;
  line-height: 140%;
  color: ${aigleColor.text.primary};
  font-weight: 500;
  margin-bottom: 4px;
`;

/**
 * Helper/Error Message 스타일
 */
export const helperText = {
  base: css`
    font-size: 12px;
    line-height: 140%;
    margin-top: 4px;
  `,
  
  default: css`
    color: ${aigleColor.text.secondary};
  `,
  
  error: css`
    color: ${aigleColor.semantic.error};
  `,
};

/**
 * Input 상태별 스타일
 */
export const inputState = {
  /** Default 상태 */
  default: css`
    ${baseInputStyle}
    background-color: ${aigleColor.background.default};
    border: 1px solid ${aigleColor.border.default};
    
    &:hover:not(:disabled) {
      border-color: ${aigleColor.border.focus};
    }
  `,
  
  /** Active/Focus 상태 */
  active: css`
    ${baseInputStyle}
    background-color: ${aigleColor.background.default};
    border: 1px solid ${aigleColor.brand.primary};
    
    &:focus {
      border-color: ${aigleColor.brand.primary};
    }
  `,
  
  /** Error 상태 */
  error: css`
    ${baseInputStyle}
    background-color: ${aigleColor.background.default};
    border: 1px solid ${aigleColor.semantic.error};
    
    &:focus {
      border-color: ${aigleColor.semantic.error};
    }
  `,
  
  /** Disabled 상태 */
  disabled: css`
    ${baseInputStyle}
    background-color: ${aigleColor.background.disabled};
    border: 1px solid ${aigleColor.border.disabled};
    color: ${aigleColor.text.disabled};
    cursor: not-allowed;
    pointer-events: none;
  `,
};

/**
 * Input Wrapper 스타일 (아이콘이나 버튼을 포함하는 경우)
 */
export const inputWrapper = css`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

/**
 * Input 내부 아이콘/버튼 스타일
 */
export const inputAdornment = {
  start: css`
    position: absolute;
    left: 12px;
    display: flex;
    align-items: center;
    pointer-events: none;
    color: ${aigleColor.grey.dark};
    
    svg {
      width: 20px;
      height: 20px;
    }
  `,
  
  end: css`
    position: absolute;
    right: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${aigleColor.grey.dark};
    
    svg {
      width: 20px;
      height: 20px;
    }
    
    button {
      padding: 0;
      margin: 0;
      background: none;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${aigleColor.grey.dark};
      pointer-events: auto;
      
      &:hover {
        color: ${aigleColor.grey.darkest};
      }
    }
  `,
};

/**
 * Input with padding for adornments
 */
export const inputWithAdornment = {
  start: css`
    padding-left: 44px;
  `,
  
  end: css`
    padding-right: 44px;
  `,
  
  both: css`
    padding-left: 44px;
    padding-right: 44px;
  `,
};

/**
 * Character Counter 스타일
 */
export const characterCounter = css`
  font-size: 12px;
  line-height: 140%;
  color: ${aigleColor.text.secondary};
  
  &.error {
    color: ${aigleColor.semantic.error};
  }
`;

/**
 * Select (Dropdown) 스타일
 */
export const selectStyles = {
  base: css`
    ${baseInputStyle}
    cursor: pointer;
    padding-right: 40px;
    background-color: ${aigleColor.background.default};
    border: 1px solid ${aigleColor.border.default};
    
    &:hover:not(:disabled) {
      border-color: ${aigleColor.border.focus};
    }
  `,
  
  icon: css`
    position: absolute;
    right: 12px;
    pointer-events: none;
    display: flex;
    align-items: center;
    color: ${aigleColor.grey.dark};
    
    svg {
      width: 20px;
      height: 20px;
    }
  `,
  
  dropdown: css`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background-color: ${aigleColor.background.default};
    border: 1px solid ${aigleColor.border.default};
    border-radius: 4px;
    box-shadow: 0 4px 12px ${aigleColor.special.shadow};
    z-index: 1000;
    max-height: 240px;
    overflow-y: auto;
  `,
  
  option: css`
    padding: 12px 16px;
    cursor: pointer;
    font-size: 16px;
    line-height: 140%;
    color: ${aigleColor.text.primary};
    
    &:hover {
      background-color: ${aigleColor.background.hover};
    }
    
    &.selected {
      background-color: ${aigleColor.alpha.primary.alpha10};
      color: ${aigleColor.brand.primary};
    }
  `,
};

/**
 * Checkbox 스타일
 */
export const checkboxStyles = {
  container: css`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;
  `,
  
  input: css`
    appearance: none;
    -webkit-appearance: none;
    width: 20px;
    height: 20px;
    border: 1px solid ${aigleColor.border.default};
    border-radius: 4px;
    background-color: ${aigleColor.background.default};
    cursor: pointer;
    position: relative;
    transition: all 0.2s ease-in-out;
    flex-shrink: 0;
    
    &:hover:not(:disabled) {
      border-color: ${aigleColor.border.focus};
    }
    
    &:checked {
      background-color: ${aigleColor.brand.primary};
      border-color: ${aigleColor.brand.primary};
      
      &::after {
        content: '';
        position: absolute;
        left: 6px;
        top: 3px;
        width: 5px;
        height: 9px;
        border: solid ${aigleColor.grey.white};
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
      }
    }
    
    &:disabled {
      background-color: ${aigleColor.background.disabled};
      border-color: ${aigleColor.border.disabled};
      cursor: not-allowed;
    }
  `,
  
  label: css`
    font-size: 14px;
    line-height: 140%;
    color: ${aigleColor.text.primary};
    cursor: pointer;
  `,
};

/**
 * Radio 스타일
 */
export const radioStyles = {
  container: css`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;
  `,
  
  input: css`
    appearance: none;
    -webkit-appearance: none;
    width: 20px;
    height: 20px;
    border: 1px solid ${aigleColor.border.default};
    border-radius: 50%;
    background-color: ${aigleColor.background.default};
    cursor: pointer;
    position: relative;
    transition: all 0.2s ease-in-out;
    flex-shrink: 0;
    
    &:hover:not(:disabled) {
      border-color: ${aigleColor.border.focus};
    }
    
    &:checked {
      border-color: ${aigleColor.brand.primary};
      
      &::after {
        content: '';
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background-color: ${aigleColor.brand.primary};
      }
    }
    
    &:disabled {
      background-color: ${aigleColor.background.disabled};
      border-color: ${aigleColor.border.disabled};
      cursor: not-allowed;
    }
  `,
  
  label: css`
    font-size: 14px;
    line-height: 140%;
    color: ${aigleColor.text.primary};
    cursor: pointer;
  `,
};

/**
 * Search Input 스타일
 */
export const searchInputStyles = {
  wrapper: css`
    ${inputWrapper}
  `,
  
  input: css`
    ${baseInputStyle}
    ${inputWithAdornment.end}
    background-color: ${aigleColor.background.default};
    border: 1px solid ${aigleColor.border.default};
    
    &:hover:not(:disabled) {
      border-color: ${aigleColor.border.focus};
    }
    
    &:focus {
      border-color: ${aigleColor.brand.primary};
    }
  `,
  
  icon: css`
    ${inputAdornment.end}
    pointer-events: none;
  `,
};

/**
 * Floating Label 스타일 (Material UI 스타일)
 */
export const floatingLabel = css`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  color: ${aigleColor.text.caption};
  pointer-events: none;
  transition: all 0.2s ease-in-out;
  background-color: ${aigleColor.background.default};
  padding: 0 4px;
  
  &.active {
    top: 0;
    font-size: 12px;
    color: ${aigleColor.brand.primary};
  }
  
  &.error {
    color: ${aigleColor.semantic.error};
  }
`;

/**
 * 통합 Input 스타일 가이드 객체
 */
export const aigleInputStyles = {
  base: baseInputStyle,
  container: inputContainer,
  label: inputLabel,
  helperText: helperText,
  state: inputState,
  wrapper: inputWrapper,
  adornment: inputAdornment,
  withAdornment: inputWithAdornment,
  counter: characterCounter,
  select: selectStyles,
  checkbox: checkboxStyles,
  radio: radioStyles,
  search: searchInputStyles,
  floatingLabel: floatingLabel,
};

/**
 * TypeScript 타입 정의
 */
export type InputStateStyles = typeof inputState;
export type SelectStyles = typeof selectStyles;
export type CheckboxStyles = typeof checkboxStyles;
export type RadioStyles = typeof radioStyles;
export type SearchInputStyles = typeof searchInputStyles;
export type AigleInputStyles = typeof aigleInputStyles;

// Default export
export default aigleInputStyles;

